import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { updateReportParticipants } from "../../../../api/service/report";
import { ReportAccessDialog } from "./ReportAccessDialog";
import { WorkOrderParticipantStatus } from "../../workOrders/utils";

/**
 * Tilknyttede utførende (utover ansvarlig utførende på avtalen).
 */
function reportUpdateErrorMessage(res, fallback) {
  if (!res?.response) return fallback;
  const data = res.response?.data;
  if (Array.isArray(data?.errors)) return data.errors.join(", ");
  if (typeof data?.error === "string") return data.error;
  return fallback;
}

export const ReportAccessesSection = ({
  reportId,
  participants = [],
  onParticipantsChange,
  canManage,
  executingEmployeeId,
  className = "",
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleDelete = async (participantId) => {
    if (!window.confirm(t("report.participants.delete_confirm"))) return;

    try {
      setError(null);
      const updated = (participants || []).map((p) =>
        p.id === participantId ? { ...p, delete: true } : p
      );
      const saveRes = await updateReportParticipants(reportId, updated);
      if (saveRes?.response) {
        setError(
          reportUpdateErrorMessage(saveRes, t("report.participants.delete_error"))
        );
        return;
      }
      const list = saveRes?.data?.participants;
      if (!Array.isArray(list)) {
        setError(t("report.participants.delete_error"));
        return;
      }
      onParticipantsChange(list);
    } catch (err) {
      console.error(err);
      setError(t("report.participants.delete_error"));
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSuccess = (list) => {
    if (onParticipantsChange && Array.isArray(list)) {
      onParticipantsChange(list);
    }
    handleDialogClose();
  };

  return (
    <>
      <div
        className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {t("report.participants.title")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t("report.participants.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4" />
                {t("report.participants.add")}
              </button>
            )}
          </div>
        </div>
        <div className="px-5 py-4">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}
          {participants.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
              <p>{t("report.participants.none")}</p>
              {canManage && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t("report.participants.add_first")}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {participants.map((p) => (
                <div
                  key={p.id}
                  className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3 transition-colors hover:bg-gray-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-0 text-sm font-medium text-gray-900">
                        {p.user ? (
                          <Link
                            to={`/dashboard/users/${p.user.id}`}
                            className="truncate text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {p.displayTitle || p.user?.displayTitle || "—"}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </div>
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                        {p.role || "—"}
                      </span>
                    </div>
                    <div className="mt-2">
                      <WorkOrderParticipantStatus participant={p} />
                    </div>
                  </div>
                  {canManage && (
                    <div className="ml-2 flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(p);
                          setDialogOpen(true);
                        }}
                        className="inline-flex items-center rounded-md border border-gray-200 bg-white p-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        title={t("report.participants.edit")}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id)}
                        className="inline-flex items-center rounded-md border border-red-200 bg-white p-2 text-sm text-red-700 transition-colors hover:bg-red-50"
                        title={t("report.participants.delete")}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ReportAccessDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        reportId={reportId}
        access={editing}
        onSuccess={handleSuccess}
        executingEmployeeId={executingEmployeeId}
        participants={participants}
      />
    </>
  );
};
